/**
 * useAudioContext - 管理Web Audio上下文，解决浏览器自动播放策略
 * 
 * 浏览器要求音频上下文必须在用户手势（点击）后才能启动
 * 此Hook提供unlock方法，在用户首次交互时恢复音频上下文
 */

import { useState, useEffect, useRef, useCallback } from 'react';

interface UseAudioContextReturn {
  /** 音频上下文实例 */
  audioContext: AudioContext | null;
  /** 是否已解锁（可以播放音频） */
  isUnlocked: boolean;
  /** 解锁音频上下文（用户首次点击时调用） */
  unlock: () => Promise<void>;
  /** 音频上下文状态 */
  state: AudioContextState;
}

export const useAudioContext = (): UseAudioContextReturn => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [state, setState] = useState<AudioContextState>('suspended');
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    // 创建音频上下文
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (AudioContextClass) {
      try {
        audioContextRef.current = new AudioContextClass();
        setState(audioContextRef.current.state);

        // 监听状态变化
        const handleStateChange = () => {
          setState(audioContextRef.current?.state || 'suspended');
          if (audioContextRef.current?.state === 'running') {
            setIsUnlocked(true);
          }
        };

        audioContextRef.current.addEventListener('statechange', handleStateChange);

        return () => {
          audioContextRef.current?.removeEventListener('statechange', handleStateChange);
          // 不关闭audioContext，因为它可能被其他组件使用
        };
      } catch (error) {
        console.warn('[AudioContext] 浏览器音频设备不可用，音频预览功能将受限:', error);
        audioContextRef.current = null;
      }
    }
  }, []);

  const unlock = useCallback(async () => {
    if (!audioContextRef.current) return;

    if (audioContextRef.current.state === 'suspended') {
      try {
        await audioContextRef.current.resume();
        setIsUnlocked(true);
        setState('running');
        console.log('[AudioContext] 已解锁，可以播放音频');
      } catch (error) {
        console.error('[AudioContext] 解锁失败:', error);
      }
    } else if (audioContextRef.current.state === 'running') {
      setIsUnlocked(true);
    }
  }, []);

  return {
    audioContext: audioContextRef.current,
    isUnlocked,
    unlock,
    state,
  };
};

export default useAudioContext;
